// src/components/authentication/UserAccountInfo.tsx

import React from 'react';
// 👇 Import the components we need for the accordion layout
import {
  Paper,
  Typography,
  Divider,
  Button,
  Box,
  useTheme,
  useMediaQuery,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { userHasAccess } from '../../services/RoleBasedAccess';
import { useAccountContext } from '../../context/AccountContext';
import { useSession } from '../../context/SessionContext';

const UserAccountInfo: React.FC = () => {
  const { user } = useSession();
  if (!user) return null;
  const { openEditAccountModal, setIsChangePasswordOpen } = useAccountContext();

  const theme = useTheme();
  // 👇 Detect if we are on a mobile screen
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Let's create a reusable block for the account details to avoid duplication
  const accountDetailsContent = (
    <Box sx={{ pt: 2 }}>
      <Typography><strong>Email:</strong> {user?.email ?? "Not Available"}</Typography>
      <Typography><strong>Name:</strong> {`${user?.first_name ?? "Unknown"} ${user?.last_name ?? ""}`.trim()}</Typography>
      <Typography><strong>Phone Number:</strong> {user?.phone_number ?? "Not Available"}</Typography>
      <Typography><strong>Role:</strong> {user?.role ?? "Unknown"}</Typography>
      <Typography><strong>System Access:</strong> {userHasAccess(user?.role, ["Internal"]) ? "All systems" : user?.system_access?.join(", ") || "None"}</Typography>
      <Typography><strong>Account Created:</strong> {user?.createdDate ? new Date(user.createdDate).toLocaleString() : "Unknown"}</Typography>
    </Box>
  );

  // And a reusable block for the action buttons
  const actionButtons = (
    <Stack direction={isMobile ? "column" : "row"} spacing={1} width={isMobile ? '100%' : 'auto'} sx={{ mt: isMobile ? 2 : 0 }}>
      <Button
        fullWidth={isMobile}
        variant="contained"
        onClick={() => openEditAccountModal(user)}
        sx={{
          backgroundColor: (theme) => theme.palette.status.accent,
          color: '#fff',
          '&:hover': {
            backgroundColor: (theme) => theme.palette.status.accent,
            opacity: 0.9,
          },
        }}
      >
        Update
      </Button>
      <Button fullWidth={isMobile} variant="contained" color="secondary" onClick={() => setIsChangePasswordOpen(true)}>
        Change Password
      </Button>
    </Stack>
  );

  // 👇 Conditionally render the entire component
  return isMobile ? (
    // --- MOBILE ACCORDION VIEW ---
    <Accordion defaultExpanded sx={{ mb: 2, backgroundImage: 'none', boxShadow: 3, '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6">My Account</Typography>
      </AccordionSummary>
      <AccordionDetails>
        {accountDetailsContent}
        {actionButtons}
      </AccordionDetails>
    </Accordion>
  ) : (
    // --- DESKTOP PAPER VIEW (Unchanged Logic, Cleaner Layout) ---
    <Paper sx={{ padding: 3, marginBottom: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">
          Account Details
        </Typography>
        {actionButtons}
      </Box>
      <Divider sx={{ my: 2 }} />
      {accountDetailsContent}
    </Paper>
  );
};

export default UserAccountInfo;
